import React, { useEffect, useMemo, useRef } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import Suspense from '@atlaskit/react-ufo/suspense';
import { useAgentSessions } from '@atlassian/agent-sessions';
import type { AgentSessionsScope } from '@atlassian/agent-sessions/types';
import { logSentryConsoleWarning } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import { createChatAgentSessionsOptions } from '../../common/createChatAgentSessionsOptions';
import { NavigationAgentSessionsContext } from './NavigationAgentSessionsContext';

type Props = Readonly<{
	scope: AgentSessionsScope;
	children: React.ReactNode;
	conversations: readonly { id: string; isEmpty: boolean }[];
	isConversationsInitialized: boolean;
}>;

type ReaderProps = Props & {
	options: ReturnType<typeof createChatAgentSessionsOptions>;
};

const NavigationAgentSessionsReader = ({
	children,
	conversations,
	isConversationsInitialized,
	options,
}: ReaderProps) => {
	const result = useAgentSessions(options);
	const sessions = result.status === 'ready' ? result.sessions : undefined;
	const byConversation = useMemo(
		() =>
			new Map(
				sessions?.flatMap((session) => {
					const id = session.conversationChannel?.id;
					return id == null ? [] : [[id, session] as const];
				}),
			),
		[sessions],
	);
	const reported = useRef(new Set<string>());
	useEffect(() => {
		if (sessions == null || !isConversationsInitialized) return;
		for (const conversation of conversations) {
			if (
				conversation.isEmpty ||
				byConversation.has(conversation.id) ||
				reported.current.has(conversation.id)
			)
				continue;
			reported.current.add(conversation.id);
			logSentryConsoleWarning('Navigation conversation has no loaded AgentSession match', {
				conversationId: conversation.id,
			});
		}
	}, [byConversation, conversations, isConversationsInitialized, sessions]);
	return (
		<NavigationAgentSessionsContext.Provider value={byConversation}>
			{children}
		</NavigationAgentSessionsContext.Provider>
	);
};

const NavigationAgentSessionsBoundary = (props: Props) => {
	const options = createChatAgentSessionsOptions(props.scope);
	const scopeKey = JSON.stringify([
		options.accountId,
		options.cloudId,
		options.rovoActivationId,
		options.includeLocalAgentSessions,
	]);
	return (
		<ErrorBoundary key={scopeKey} fallback={<>{props.children}</>}>
			<Suspense interactionName="navigation-agent-sessions" fallback={props.children}>
				<NavigationAgentSessionsReader {...props} options={options} />
			</Suspense>
		</ErrorBoundary>
	);
};

/** Keep navigation controls and controller ownership available while enrichment loads or fails. */
export const NavigationAgentSessions = (props: Props): React.JSX.Element =>
	fg('rovo-chat-ai-inbox') ? <NavigationAgentSessionsBoundary {...props} /> : <>{props.children}</>;
